import React, { PureComponent, Fragment } from 'react'
import i18n from 'i18next'
import { observer } from 'mobx-react'
import { reaction } from 'mobx'
import * as R from 'ramda'
import ChartSelector from '../ChartSelector'
import Chart from '../Chart'
import OptionResponse from '../OptionResponse'
import DebugInfo from '../DebugInfo'
import ReportPageTitle from '../ReportPageTitle'
import { getQuestionTitle, getOptionTitle } from '../../../../../utils/QuestionHelper'
import Panel from '../../../../../UniformComponents/Panel'
import PanelHeader from '../../../../../UniformComponents/PanelHeader'
import QuestionFooter from '../QuestionFooter'
import QuestionEdit from '../QuestionEdit'

@observer
class SmartGrid extends PureComponent {
    constructor(props) {
        super(props)
        const {
            reportStore,
            dataKey,
            question,
            store,
        } = props
        const { survey } = store
        const data = reportStore.reportData.questions[dataKey]
        this.filteredCategories = R.pathOr([], ['yOptions'], data).reduce((filtered, option, index) => {
            if (option.isHidden) {
                filtered.push(index)
            }
            return filtered
        }, [])

        this.categories = question.optionSetY.options.map((option) => (
            getOptionTitle({
                langs: option.langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })
        ))

        this.state = {
            updated: new Date().getTime(),
            sorting: {
                direction: undefined,
                sortBy: undefined,
                defaultSorting: Array.from(Array(R.pathOr([], ['yOptions'], data).length).keys()),
                sortedIndexArray: Array.from(Array(R.pathOr([], ['yOptions'], data).length).keys()),
            },
            chartType: R.pathOr('column', ['settings', 'report', 'graphType'], question),
        }

        reaction(
            () => props.reportStore.reportDataFetched,
            () => {
                this.setState({
                    updated: props.reportStore.reportDataFetched,
                })
            },
        )

        reportStore.questionSorting[dataKey] = this.getQuestionSorting
    }

    componentWillUnmount() {
        const { reportStore, dataKey } = this.props
        Reflect.deleteProperty(reportStore.questionSorting, dataKey)
    }

    getQuestionSorting = () => {
        const { sorting } = this.state
        return [sorting.sortedIndexArray, 'stackedBar']
    }

    getXResponse = (data, index, optionX) => {
        const { questionTypeCode } = data
        const id = optionX.toUpperCase()
        let response = 0
        if (Object.hasOwnProperty.call(data.xOptions, id)) {
            const optionResponse = data.xOptions[id]
            response = questionTypeCode === 'RT'
                ? optionResponse.numberOfResponses[index]
                : optionResponse.pctOfResponses[index].toFixed(0)
        }
        return response
    }

    colMapper = ({ path, type, id }, data, option, index) => {
        const {
            question, reportStore, store, reportFilter,
        } = this.props
        const { survey } = store
        let col = null
        if (type === 'pct') {
            col = (
                <td>
                    {R.defaultTo(0, R.pathOr(0, [...path.split('.'), index])(data)).toFixed(2)}
                    %
                </td>
            )
        } else if (type === 'OptionResponse') {
            col = (
                <td>
                    <OptionResponse
                        id={id}
                        value={R.defaultTo(0, R.pathOr(0, [...path.split('.'), index])(data)).toFixed(2)}
                        reportStore={reportStore}
                        reportFilter={reportFilter}
                        pct
                    />
                </td>
            )
        } else if (type === 'optionTitle') {
            col = (
                <th>
                    <div>
                        {getOptionTitle({
                            langs: question.optionSetY.options[index].langs,
                            locale: reportStore.locale,
                            defaultLocale: survey.metadata.defaultLocale,
                            initialLocale: question.metadata.initialLocale,
                        })}
                    </div>
                </th>
            )
        } else if (type === 'val') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data)}
                </td>
            )
        } else if (type === 'avg') {
            col = (
                <td>
                    {R.pathOr(0, [...path.split('.'), index])(data).toFixed(2)}
                </td>
            )
        } else {
            col = (
                <td />
            )
        }
        return col
    }

    sortBy = (col) => {
        const sorting = R.clone(this.state.sorting)
        const { reportStore, dataKey } = this.props
        const data = reportStore.reportData.questions[dataKey]

        if (col.sortable === false) {
            return
        }

        const oldSortBy = sorting.sortBy

        let values = []
        if (!col.path) {
            sorting.sortBy = 'label'
            values = data.yOptions.reduce((newOptions, o) => {
                newOptions.push(o.label)
                return newOptions
            }, [])
        } else {
            sorting.sortBy = col.path
            values = R.pathOr(0, col.path.split('.'))(data)
        }
        const sortedIndexArray = Array
            .from(Array(values.length).keys())
            .sort((a, b) => (values[a] < values[b] ? -1 : values[a] > values[b] ? 1 : 0))

        if (sorting.sortBy !== oldSortBy || !sorting.direction) {
            sorting.direction = 'ASC'
        } else {
            sorting.direction = sorting.direction === 'ASC'
                ? 'DESC'
                : undefined
        }

        if (sorting.direction === 'ASC') {
            sorting.sortedIndexArray = sortedIndexArray
        } else if (sorting.direction === 'DESC') {
            sorting.sortedIndexArray = sortedIndexArray.reverse()
        } else {
            sorting.sortedIndexArray = sorting.defaultSorting
        }

        this.setState({
            sorting,
        })
    }

    sortIcon = ({ path }) => {
        const { sorting } = this.state
        const { direction, sortBy } = sorting
        let icon = <span style={{ display: 'inline' }} />
        if (
            direction
            && (
                !path
                    && sortBy === 'label'
                    || path === sortBy
            )
        ) {
            const iconClass = direction === 'ASC'
                ? 'fal fa-long-arrow-up'
                : 'fal fa-long-arrow-down'
            icon = <i style={{ marginLeft: '5px', width: '6px', color: '#81859B' }} className={`${iconClass}`} />
        }

        return icon
    }

    getChartCategories = () => {
        const { sorting } = this.state
        const { sortedIndexArray } = sorting
        return sortedIndexArray.map((sortedIndex) => this.categories[sortedIndex])
    }

    generateDataTable = () => {
        const { sorting, chartType } = this.state
        const {
            store, question, reportStore, reportFilter, dataKey,
        } = this.props
        const { survey } = store
        const data = R.or(reportStore.reportData.questions[dataKey], {})

        const benchmarkCols = question.metadata.isBenchmarked
            ? Array.from(reportStore.benchmarks2)
                .filter(([key, { value }]) => (value && R.hasPath(['aggregate', 'benchmarks', key], data)))
                .map(([key]) => ({
                    label: i18n.t(`report_${key}_benchmark`),
                    path: `benchmarks.${key}`,
                    type: 'empty',
                    sortable: false,
                    colorKey: key,
                }))
            : []

        const optionSetXColumns = question.optionSetX.options.map((option) => ({
            label: `${getOptionTitle({
                langs: option.langs,
                locale: reportStore.locale,
                defaultLocale: survey.metadata.defaultLocale,
                initialLocale: question.metadata.initialLocale,
            })} (${option.reportingValue})`,
            id: `${question.pageQuestionId}_${option.id}_${option.id}`,
            path: `xOptions.${option.id.toUpperCase()}.pctOfResponses`,
            type: 'OptionResponse',
        }))
        const averages = [{
            label: 'Average',
            type: 'avg',
            path: 'aggregate.average',
            colorKey: 'survey',
        }]

        const cols = [
            {
                label: i18n.t('report_option_name'),
                class: 'tleft',
                type: 'optionTitle',
            },
            {
                label: i18n.t('report_question_responses'),
                path: 'numberOfResponses',
                type: 'val',
            },
            ...optionSetXColumns,
            {
                label: '% Agree',
                path: 'agreed',
                type: 'pct',
            },
            ...averages,
            ...benchmarkCols,
        ]

        this.cols = averages

        return (
            <Fragment>
                <div style={{ overflowX: 'auto' }}>
                    <table className="datatable question tablesorter tablesorter-default">
                        <thead>
                            <tr className="col tablesorter-headerRow">
                                {cols
                                    .map((col) => (
                                        <th style={{ cursor: 'pointer' }} className={`${col.class || ''} tablesorter-header`} onClick={() => this.sortBy(col)}>
                                            <div className="tablesorter-header-inner">
                                                {col.label}
                                                {this.sortIcon(col)}
                                            </div>
                                        </th>
                                    ))}
                                <td className="tlast" />
                            </tr>
                        </thead>
                        <tbody>
                            {
                                sorting.sortedIndexArray.map((sortedIndex) => {
                                    const option = data.yOptions[sortedIndex]
                                    return (
                                        <tr className="col" key={`option_${option.id}`}>
                                            {cols
                                                .map((col) => (
                                                    this.colMapper(col, data, option, sortedIndex)
                                                ))}
                                            <td className="tlast" />
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                        <tfoot>
                            <tr className="col">
                                <th>
                                    {i18n.t(`dashboard.${question.metadata.kpiType}.title`)}
                                    :
                                </th>
                                <td />
                                {question.optionSetX.options.map(() => (
                                    <td />
                                ))}
                                <td className="stats" />
                                <td className="stats">{R.pathOr(0, ['aggregate', 'sgAverage'], data).toFixed(2)}</td>
                                {
                                    question.metadata.isBenchmarked
                                        && Array.from(reportStore.benchmarks2).map(([key, { value }]) => (
                                            value && R.hasPath(['aggregate', 'benchmarks', key], data)
                                                ? <td className="stats">{R.pathOr(0, ['aggregate', 'benchmarks', key], data).toFixed(2)}</td>
                                                : null
                                        ))
                                }
                                <td className="tlast" />
                            </tr>
                        </tfoot>
                    </table>
                </div>
                {data.hasResponses && (
                    <Fragment>
                        <ChartSelector
                            chartType={chartType}
                            onChange={(chartType) => { this.setState({ chartType }) }}
                        />
                        <Chart
                            colors={this.chartColours}
                            reportFilter={reportFilter}
                            survey={survey}
                            dataKey={dataKey}
                            question={question}
                            data={data}
                            sortedIndexArray={this.state.sorting.sortedIndexArray}
                            pageQuestionId={question.pageQuestionId}
                            id={question.pageQuestionId}
                            chartType={chartType}
                            series={this.cols}
                            categorySet={this.categories}
                            categories={this.getChartCategories()}
                            questionTypeCode={question.questionTypeCode}
                            kpiType={question.metadata.kpiType}
                            reportStore={reportStore}
                            filtered={this.filteredCategories}
                        />
                        <QuestionFooter
                            dataKey={dataKey}
                            reportStore={reportStore}
                        />
                    </Fragment>
                )}
            </Fragment>
        )
    }

    npsScore = () => {
        const { reportStore, dataKey } = this.props
        const data = reportStore.reportData.questions[dataKey]

        return (
            <div>
                <Panel cssClass="kpi stats">
                    <div className="iconCircle">
                        <i className="fa-light fa-gauge" />
                    </div>
                    <div className="details">
                        <label>Your score</label>
                        <span>{R.pathOr(0, ['aggregate', 'sgAverage'], data).toFixed(2)}</span>
                    </div>
                </Panel>
            </div>
        )
    }

    render() {
        const {
            question,
            store,
            reportStore,
            pageId,
        } = this.props
        const { survey } = store

        return (
            <Panel cssClass="question">
                <PanelHeader>
                    <div id={`questionTitle_${question.pageQuestionId}`} className="pageTitleContainer">
                        <ReportPageTitle
                            h2={getQuestionTitle({
                                content: question.content.shortTitle,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            h3={getQuestionTitle({
                                content: question.content.longTitle,
                                locale: reportStore.locale,
                                defaultLocale: survey.metadata.defaultLocale,
                                initialLocale: question.metadata.initialLocale,
                            })}
                            reportStore={reportStore}
                            questionTypeCode={question.questionTypeCode}
                        />
                        <QuestionEdit reportStore={reportStore} pageId={pageId} />
                        {this.npsScore()}
                    </div>
                </PanelHeader>
                <DebugInfo
                    store={store}
                    questionTypeCode={question.questionTypeCode}
                    reportStore={reportStore}
                    questionId={question.id}
                    pageQuestionId={question.pageQuestionId}
                />
                {this.generateDataTable()}
            </Panel>
        )
    }
}

export default SmartGrid
